import React, { useEffect, useState } from "react";
import {
  Autocomplete,
  TextField,
} from "@mui/material";
import ImageNotSupportedIcon from "@mui/icons-material/ImageNotSupported";
import AppBreadcrumbs from "../../components/AppBreadcrumbs";
import SearchIcon from "@mui/icons-material/Search";
import * as services from "./../../services/saleng";
import { useNavigate, useSearchParams } from "react-router-dom";
import { statusColors } from "../../utils/statusColor";
import DescriptionIcon from "@mui/icons-material/Description";

/* ---------- 16:9 ratio image wrapper ---------- */
const RatioImage = ({ src, alt }) => {
  return (
    <div className="relative w-full pt-[56.25%] overflow-hidden bg-gray-100 flex-shrink-0">
      {src ? (
        <img
          src={src}
          alt={alt || "preview"}
          className="absolute inset-0 w-full h-full object-cover block"
        />
      ) : (
        <div className="absolute inset-0 flex items-center justify-center">
          <ImageNotSupportedIcon style={{ fontSize: 40, color: '#9ca3af' }} />
        </div>
      )}
    </div>
  );
};

const CardItem = ({ item, onToggle, onClick, status }) => {
  const latestImage =
    item.images && item.images.length > 0
      ? [...item.images].sort(
        (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
      )[0].imageUrl
      : null;

  const getStatusStyles = (s) => {
    switch (s) {
      case "waiting":
        return "bg-blue-50 border-blue-100 ring-1 ring-blue-100";
      case "payed":
        return "bg-green-50 border-green-100 ring-1 ring-green-100";
      case "cancel":
        return "bg-red-50 border-red-100 ring-1 ring-red-100";
      default:
        return "bg-white border-gray-100";
    }
  };

  return (
    <div
      className={`group relative w-full cursor-pointer flex flex-col rounded-2xl shadow-sm border transition-all duration-300 hover:shadow-xl hover:-translate-y-1 overflow-hidden h-full ${getStatusStyles(status)}`}
      onClick={() => onClick(item)}
    >
      {/* Cover Image */}
      <RatioImage src={latestImage} alt="preview" />

      {/* Overlapping Driver Image & Main Info Header */}
      <div className="px-5 relative flex items-end -mt-12 mb-4 z-10 pointer-events-none">
        {/* Driver Image */}
        <div className="w-[100px] h-[125px] rounded-xl overflow-hidden shadow-md border-[4px] border-white bg-gray-200 flex-shrink-0 relative">
          {item.driver?.img ? (
            <img
              src={item.driver.img}
              alt="driver"
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gray-100 text-gray-300">
              <ImageNotSupportedIcon style={{ fontSize: 32 }} />
            </div>
          )}
        </div>

        {/* Quick Stats or Bill Num Right */}
        <div className="ml-auto mb-1">
          <div className="bg-white/90 backdrop-blur-sm border border-gray-100 px-3 py-1.5 rounded-lg shadow-sm text-sm font-bold text-gray-700">
            {item?.items?.length || 0} รายการ
          </div>
        </div>
      </div>

      {/* Body Content */}
      <div className="px-5 pb-5 flex-1 flex flex-col space-y-4">

        {/* Bill & Customer */}
        <div>
          <div className="flex items-center justify-between">
            <h3 className="text-2xl font-bold text-gray-900 leading-tight">
              #{(item.billNumber || "").split("-").pop()}
            </h3>
          </div>
          <div className="text-lg font-bold text-gray-800 mt-1 line-clamp-1">
            {item.customer?.name || item.customerName || "-"}
          </div>
          <div className="text-base text-gray-600 mt-0.5 line-clamp-1">
            คนขับ: {item.driver?.name || ""} {item.driver?.surname || ""}
          </div>
        </div>

        {/* Vehicle Badge */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="bg-white border-2 border-gray-200 rounded-lg px-3 py-1 flex flex-col items-center min-w-[70px]">
            <span className="text-xs text-gray-500 leading-none mb-0.5">ทะเบียน</span>
            <span className="text-base font-bold text-gray-900 leading-none">
              {item.car?.license || "-"}
            </span>
          </div>
          <div className="text-sm text-gray-600">
            <div className="font-medium">{item.car?.province || "ไม่ระบุจังหวัด"}</div>
            <div className="opacity-75 text-xs">{item.car?.type || ""}</div>
          </div>
        </div>

        {/* Note */}
        {Boolean(item?.note) && (
          <div className="bg-gray-100/50 rounded-lg p-3 flex gap-2 items-start">
            <DescriptionIcon style={{ fontSize: 20, marginTop: 2 }} className="text-gray-400" />
            <p className="text-sm text-gray-700 line-clamp-2 leading-relaxed">
              {item.note}
            </p>
          </div>
        )}

        <div className="flex-grow" />

        {/* Footer Dates */}
        <div className="pt-4 mt-2 border-t border-gray-200/50 text-sm text-gray-500 space-y-1.5">
          <div className="flex justify-between">
            <span>สร้าง:</span>
            <span className="font-medium text-gray-700">
              {new Date(item.createdAt).toLocaleDateString("th-TH", { day: 'numeric', month: 'short', year: '2-digit' })}
              {" "}{new Date(item.createdAt).toLocaleTimeString("th-TH", { hour: '2-digit', minute: '2-digit' })}
            </span>
          </div>
          {status === "payed" && item.paidAt && (
            <div className="flex justify-between text-green-700">
              <span>จ่าย:</span>
              <span className="font-medium">
                {new Date(item.paidAt).toLocaleDateString("th-TH", { day: 'numeric', month: 'short', year: '2-digit' })}
                {" "}{new Date(item.paidAt).toLocaleTimeString("th-TH", { hour: '2-digit', minute: '2-digit' })}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Selection Checkbox (Absolute Top Right) */}
      <div className="absolute top-4 right-4 z-20">
        <div
          onClick={(e) => {
            e.stopPropagation();
            onToggle(item.id);
          }}
          className={`w-9 h-9 rounded-full border-2 bg-white flex items-center justify-center cursor-pointer shadow-md transition-all hover:scale-110 active:scale-95 ${item.selected || item.isWeighed
            ? 'border-blue-500 text-blue-500'
            : 'border-gray-200 text-transparent hover:border-gray-300'
            }`}
        >
          <div className={`w-5 h-5 rounded-full ${item.selected || item.isWeighed ? 'bg-blue-500' : ''}`} />
        </div>
      </div>
    </div>
  );
};

export default function ImageCardGrid() {
  const [isLoading, setIsLoading] = useState(true);
  const [transaction, setTransaction] = useState([]);
  const [search, setSearch] = useState("");
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const status = searchParams.get("status");

  const formatDate = (date) => date.toISOString().split("T")[0];

  // ----------- อ่านค่าจาก URL หรือ fallback default -----------
  const initialStartDate =
    searchParams.get("start") ||
    (() => {
      const d = new Date();
      if (status !== "payed") d.setMonth(d.getMonth() - 1);
      return formatDate(d);
    })();

  const initialEndDate =
    searchParams.get("end") || formatDate(new Date());

  const [startDate, setStartDate] = useState(initialStartDate);
  const [endDate, setEndDate] = useState(initialEndDate);

  // ----------- sync เมื่อผู้ใช้เลือกวันที่ใหม่ -----------
  const handleStartDateChange = (v) => {
    setStartDate(v);
    setSearchParams({
      status,
      start: v,
      end: endDate,
    });
  };

  const handleEndDateChange = (v) => {
    setEndDate(v);
    setSearchParams({
      status,
      start: startDate,
      end: v,
    });
  };

  // ----------- ดึงข้อมูลตามวันที่ใน URL -----------
  useEffect(() => {
    let interval;

    const fetchData = async () => {
      setIsLoading(true);
      try {
        const data = await services.getTransactionByStatus(
          status,
          startDate,
          endDate
        );
        setTransaction(data);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();

    interval = setInterval(fetchData, 30_000);

    return () => clearInterval(interval);
  }, [status, startDate, endDate]);

  // ----------- ดึงข้อมูลสินค้า -----------
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const res = await services.getProductsBranch(true);
        setProducts(res.data?.data || []);
      } catch (err) {
        console.error("fetch products error", err);
      }
    };
    fetchProducts();
  }, []);

  const handleToggle = (id) => {
    setTransaction((prev) =>
      prev.map((item) =>
        item.id === id ? { ...item, selected: !item.selected } : item
      )
    );
  };

  const handleTicketSelect = (data) => {
    navigate(
      `/buy/checkout?ticket=${data.billNumber}&id=${data.transactionId}&status=${data.status}`
    );
    // Note: Original code had id=${data.transactionId}&ticket=... check order if important. 
    // Original: /buy/checkout?id=${data.transactionId}&ticket=${data.billNumber}&status=${data.status}
    // Updated to match original URL structure to be safe.
  };

  const filtered = (transaction || []).filter((item) => {
    const query = search.trim().toLowerCase();
    const normalizedQuery = query.replace(/^#/, "");

    // --- Product Filter ---
    const matchesProduct = !selectedProduct ||
      (item.items || []).some(sub => String(sub.productId || sub.product?.id) === String(selectedProduct.id));

    if (!matchesProduct) return false;

    if (!query) return true;

    const fields = [
      item.id,
      item.billNumber,
      item.customer?.name,
      item.customerName,
      item.driver?.name,
      item.driver?.surname,
      item.car?.license,
      item.car?.type,
      item.car?.province,
      item.note,
      ...(item.items || []).map(it => it.product?.title || it.productName || ""),
      item.createdAt
        ? new Date(item.createdAt).toLocaleString("th-TH")
        : "",
    ];

    const carTokens =
      item.car?.license?.match(/[ก-ฮ]+|[0-9]+/g)?.map((s) => s.toLowerCase()) ||
      [];

    return (
      fields.some(
        (field) =>
          typeof field === "string" &&
          field.toLowerCase().includes(normalizedQuery)
      ) || carTokens.some((token) => token.includes(normalizedQuery))
    );
  });

  return (
    <div className="min-h-[calc(100vh-64px)] bg-gray-50 p-4 md:p-8">
      <AppBreadcrumbs />

      {/* ฟิลเตอร์ */}
      <div className="flex flex-col xl:flex-row gap-4 my-6 items-end justify-between bg-white p-5 rounded-2xl shadow-sm border border-gray-100">

        {/* Left: Search & Product */}
        <div className="flex flex-col md:flex-row gap-4 w-full xl:w-auto flex-1">
          <div className="flex-1 md:max-w-md">
            <label className="block mb-1 text-xs font-bold text-gray-500 uppercase tracking-wider ml-1">ค้นหาด่วน</label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <SearchIcon className="text-gray-400" />
              </div>
              <input
                type="text"
                placeholder="เลขที่บิล, ชื่อลูกค้า, ทะเบียนรถ..."
                className="bg-gray-50 border border-gray-200 text-gray-900 text-sm rounded-xl focus:ring-2 focus:ring-green-500/20 focus:border-green-500 block w-full pl-10 p-2.5 outline-none transition-all"
                value={search}
                onChange={(e) => setSearch(e.target.value.trimStart())}
              />
            </div>
          </div>

          <div className="w-full md:w-72">
            <label className="block mb-1 text-xs font-bold text-gray-500 uppercase tracking-wider ml-1">กรองสินค้า</label>
            <Autocomplete
              size="small"
              options={products}
              getOptionLabel={(option) => option.title || ""}
              value={selectedProduct}
              onChange={(_, newValue) => setSelectedProduct(newValue)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="สินค้าทั้งหมด..."
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      backgroundColor: '#f9fafb', // gray-50
                      borderRadius: '12px',
                      fontSize: '0.875rem',
                      '& fieldset': {
                        borderColor: '#e5e7eb', // gray-200
                      },
                      '&:hover fieldset': {
                        borderColor: '#10b981', // green-500
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#10b981',
                        borderWidth: '1px',
                      }
                    }
                  }}
                />
              )}
            />
          </div>
        </div>

        {/* Right: Dates */}
        <div className="flex flex-row gap-3 w-full xl:w-auto">
          <div className="flex-1 md:w-40">
            <label className="block mb-1 text-xs font-bold text-gray-500 uppercase tracking-wider ml-1">ตั้งแต่วันที่</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => handleStartDateChange(e.target.value)}
              className="bg-gray-50 border border-gray-200 text-gray-900 text-sm rounded-xl focus:ring-2 focus:ring-green-500/20 focus:border-green-500 block w-full p-2 outline-none transition-all h-[40px]"
            />
          </div>
          <div className="flex-1 md:w-40">
            <label className="block mb-1 text-xs font-bold text-gray-500 uppercase tracking-wider ml-1">ถึงวันที่</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => handleEndDateChange(e.target.value)}
              className="bg-gray-50 border border-gray-200 text-gray-900 text-sm rounded-xl focus:ring-2 focus:ring-green-500/20 focus:border-green-500 block w-full p-2 outline-none transition-all h-[40px]"
            />
          </div>
        </div>
      </div>

      {/* Grid */}
      <div className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-4 md:gap-6 ${isLoading ? 'opacity-60 pointer-events-none' : ''}`}>
        {filtered.map((item) => (
          <CardItem
            key={item.id}
            item={item}
            onToggle={handleToggle}
            onClick={() => handleTicketSelect(item)} // Fix: pass item directly to match helper
            status={status}
          />
        ))}
      </div>

      {!isLoading && filtered.length === 0 && (
        <div className="text-center text-gray-500 py-12 bg-white rounded-xl border border-gray-200 shadow-sm mt-4">
          <p>ไม่พบรายการตามเงื่อนไขที่ค้นหา</p>
        </div>
      )}
    </div>
  );
}
